/**
 * The canonical event model for ReplayOps.
 *
 * We build on rrweb's proven recording taxonomy but own the wire envelope so we
 * can evolve batching, privacy, and metadata independently of the recorder. The
 * numeric enums are stable across versions — never renumber them, only append.
 */
/** Top-level classification of a recorded event. Mirrors rrweb `EventType`. */
export var EventType;
(function (EventType) {
    EventType[EventType["DomContentLoaded"] = 0] = "DomContentLoaded";
    EventType[EventType["Load"] = 1] = "Load";
    EventType[EventType["FullSnapshot"] = 2] = "FullSnapshot";
    EventType[EventType["IncrementalSnapshot"] = 3] = "IncrementalSnapshot";
    EventType[EventType["Meta"] = 4] = "Meta";
    EventType[EventType["Custom"] = 5] = "Custom";
    EventType[EventType["Plugin"] = 6] = "Plugin";
    // ── Native (iOS / Android) ──
    // Native apps have no DOM, so they record rendered frames plus the gestures
    // between them instead of mutations. Appended, never renumbered: a web
    // session recorded before these existed still replays unchanged.
    /** A captured screen image — already downscaled and masked on the device. */
    EventType[EventType["NativeFrame"] = 7] = "NativeFrame";
    /** A touch, scroll or screen transition between frames. */
    EventType[EventType["NativeInteraction"] = 8] = "NativeInteraction";
})(EventType || (EventType = {}));
/** Sub-source of an incremental snapshot. Mirrors rrweb `IncrementalSource`. */
export var IncrementalSource;
(function (IncrementalSource) {
    IncrementalSource[IncrementalSource["Mutation"] = 0] = "Mutation";
    IncrementalSource[IncrementalSource["MouseMove"] = 1] = "MouseMove";
    IncrementalSource[IncrementalSource["MouseInteraction"] = 2] = "MouseInteraction";
    IncrementalSource[IncrementalSource["Scroll"] = 3] = "Scroll";
    IncrementalSource[IncrementalSource["ViewportResize"] = 4] = "ViewportResize";
    IncrementalSource[IncrementalSource["Input"] = 5] = "Input";
    IncrementalSource[IncrementalSource["TouchMove"] = 6] = "TouchMove";
    IncrementalSource[IncrementalSource["MediaInteraction"] = 7] = "MediaInteraction";
    IncrementalSource[IncrementalSource["StyleSheetRule"] = 8] = "StyleSheetRule";
    IncrementalSource[IncrementalSource["CanvasMutation"] = 9] = "CanvasMutation";
    IncrementalSource[IncrementalSource["Font"] = 10] = "Font";
    IncrementalSource[IncrementalSource["Selection"] = 11] = "Selection";
    IncrementalSource[IncrementalSource["StyleDeclaration"] = 12] = "StyleDeclaration";
    IncrementalSource[IncrementalSource["Drag"] = 13] = "Drag";
})(IncrementalSource || (IncrementalSource = {}));
/** Discrete mouse/pointer interactions within an incremental snapshot. */
export var MouseInteractionType;
(function (MouseInteractionType) {
    MouseInteractionType[MouseInteractionType["MouseUp"] = 0] = "MouseUp";
    MouseInteractionType[MouseInteractionType["MouseDown"] = 1] = "MouseDown";
    MouseInteractionType[MouseInteractionType["Click"] = 2] = "Click";
    MouseInteractionType[MouseInteractionType["ContextMenu"] = 3] = "ContextMenu";
    MouseInteractionType[MouseInteractionType["DblClick"] = 4] = "DblClick";
    MouseInteractionType[MouseInteractionType["Focus"] = 5] = "Focus";
    MouseInteractionType[MouseInteractionType["Blur"] = 6] = "Blur";
    MouseInteractionType[MouseInteractionType["TouchStart"] = 7] = "TouchStart";
    MouseInteractionType[MouseInteractionType["TouchEnd"] = 9] = "TouchEnd";
})(MouseInteractionType || (MouseInteractionType = {}));
/** Plugin channels for auxiliary telemetry riding alongside DOM replay. */
export var PluginName;
(function (PluginName) {
    PluginName["Console"] = "replayops/console";
    PluginName["Network"] = "replayops/network";
    PluginName["Performance"] = "replayops/performance";
    PluginName["WebVitals"] = "replayops/webvitals";
    PluginName["Error"] = "replayops/error";
})(PluginName || (PluginName = {}));
/** What the user did between two native frames. */
export var NativeInteractionType;
(function (NativeInteractionType) {
    NativeInteractionType[NativeInteractionType["Tap"] = 0] = "Tap";
    NativeInteractionType[NativeInteractionType["DoubleTap"] = 1] = "DoubleTap";
    NativeInteractionType[NativeInteractionType["LongPress"] = 2] = "LongPress";
    NativeInteractionType[NativeInteractionType["Scroll"] = 3] = "Scroll";
    NativeInteractionType[NativeInteractionType["Swipe"] = 4] = "Swipe";
    /** Navigation to another screen — the native equivalent of a page view. */
    NativeInteractionType[NativeInteractionType["ScreenChange"] = 5] = "ScreenChange";
    NativeInteractionType[NativeInteractionType["KeyboardInput"] = 6] = "KeyboardInput";
    NativeInteractionType[NativeInteractionType["Back"] = 7] = "Back";
})(NativeInteractionType || (NativeInteractionType = {}));
//# sourceMappingURL=events.js.map